import { Lightbulb, CheckCircle2, XCircle, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MarkdownText } from '@/components/ui/markdown-text'
import type { Hint } from '@/data/hints'

interface HintCardProps {
  hint: Hint
}

export function HintCard({ hint }: HintCardProps) {
  const { t } = useTranslation('conversations')

  // Get translated content using hint.key
  const title = t(`hints.items.${hint.key}.title`)
  const description = t(`hints.items.${hint.key}.description`)
  const good_example = hint.hasGoodExample
    ? t(`hints.items.${hint.key}.goodExample`)
    : undefined
  const bad_example = hint.hasBadExample
    ? t(`hints.items.${hint.key}.badExample`)
    : undefined
  const neutral_example = hint.hasNeutralExample
    ? t(`hints.items.${hint.key}.neutralExample`)
    : undefined

  return (
    <div className="w-full flex items-center justify-center">
      <Card className="w-full max-w-md border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl hover:bg-white dark:hover:bg-slate-800">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex size-9 items-center justify-center rounded-lg bg-amber-500 shadow-md">
              <Lightbulb className="size-5 text-white" strokeWidth={2.5} />
            </div>
            <CardTitle className="text-xl font-bold text-slate-900 dark:text-white">{title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <MarkdownText className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{description}</MarkdownText>
          {(good_example || bad_example || neutral_example) && (
            <>
              <div className="mb-6" />
              <div className="space-y-3">
                {good_example && (
                  <div className="flex gap-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 p-4">
                    <CheckCircle2 className="size-5 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">{t('hints.goodExample')}</p>
                      <MarkdownText className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{good_example}</MarkdownText>
                    </div>
                  </div>
                )}
                {bad_example && (
                  <div className="flex gap-3 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950 p-4">
                    <XCircle className="size-5 shrink-0 text-rose-600 dark:text-rose-400 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-rose-900 dark:text-rose-200">{t('hints.badExample')}</p>
                      <MarkdownText className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{bad_example}</MarkdownText>
                    </div>
                  </div>
                )}
                {neutral_example && (
                  <div className="flex gap-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-4">
                    <Info className="size-5 shrink-0 text-slate-600 dark:text-slate-400 mt-0.5" />
                    <div className="space-y-1">
                      <MarkdownText className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{neutral_example}</MarkdownText>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
